import { BOND_SIZE_ROUNDING_STEP } from "@/lib/businessRules/minimumSizeRule";
import { findBondSecurityByIsin } from "@/store/bondSecuritiesStoreACTIONS";
import { returnBondSecurityChanges } from "../helper/bondTicketHelper";
import { ITicketFieldsProps } from "../helper/ticketSharedHelper";
import { useBondSecuritySearch } from "../helper/useBondSecuritySearch";
import DirectionSelect from "../ticketFields/DirectionSelect";
import NominalInput from "../ticketFields/NominalInput";
import ReferenceDataSelect, {
  IReferenceDataOption,
} from "../ticketFields/ReferenceDataSelect";
import TicketDateInput from "../ticketFields/TicketDateInput";
import TicketRow from "../ticketFields/TicketRow";
import AllocationSplits from "../ticketFields/AllocationSplits";
import TicketExternalIdRow from "../ticketFields/TicketExternalIdRow";
import { useBondTicket } from "./useBondTicket";
import { returnSettlementHolidayCalendars } from "../helper/holidayCalendarsHelper";
import {
  NO_RISK_MESSAGE,
  returnDV01SizeChanges,
  returnTicketDV01,
  returnTicketRisk,
} from "../helper/dv01TicketHelper";
import DV01Input from "../ticketFields/DV01Input";
import { IBondTicket, TicketInstrument } from "@/store/tradeFromScratchStore";

// The bond fields a Trade Creation leg row shows inline. Leg Creation stacks them above
// BondDetailFields.
const BondCoreFields = ({ ticketId, highlightedKeys }: ITicketFieldsProps) => {
  const { ticket, updateBondTicket, requiredKeys } = useBondTicket(ticketId);
  const { searchIsinOptions, searchSecurityNameOptions } =
    useBondSecuritySearch();

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);

  const handleSecuritySelect = (option: IReferenceDataOption | null) =>
    updateBondTicket(
      returnBondSecurityChanges(
        ticket.tradeDate,
        option ? findBondSecurityByIsin(option.value) : undefined,
        option?.value ?? "",
      ),
    );

  return (
    <div
      data-testid="BondCoreFieldsContainer"
      id="BondCoreFieldsContainer"
      className="flex flex-col gap-2"
    >
      <TicketRow
        label="ISIN"
        testId="BondTicketIsinRow"
        isHighlighted={isMissing("isin")}
        isRequired={isRequired("isin")}
      >
        <ReferenceDataSelect
          testId="BondTicketIsinInput"
          placeholder="Search ISIN ..."
          selected={
            ticket.isin ? { label: ticket.isin, value: ticket.isin } : null
          }
          searchOptions={searchIsinOptions}
          onSelect={handleSecuritySelect}
        />
      </TicketRow>

      <TicketRow
        label="Security Name"
        testId="BondTicketSecurityNameRow"
        isHighlighted={isMissing("isin")}
        isRequired={isRequired("isin")}
      >
        <ReferenceDataSelect
          testId="BondTicketSecurityNameInput"
          placeholder="Search security ..."
          selected={
            ticket.isin
              ? { label: ticket.securityName, value: ticket.isin }
              : null
          }
          searchOptions={searchSecurityNameOptions}
          onSelect={handleSecuritySelect}
        />
      </TicketRow>

      <TicketRow
        label="Nominal"
        testId="BondTicketNominalRow"
        isHighlighted={isMissing("nominal")}
        isRequired={isRequired("nominal")}
      >
        <NominalInput
          testId="BondTicketNominalInput"
          value={ticket.nominal}
          onCommit={(nominal) => updateBondTicket({ nominal })}
          roundingStep={BOND_SIZE_ROUNDING_STEP}
        />
      </TicketRow>

      <TicketRow label="Risk (DV01)" testId="BondTicketDV01Row">
        <DV01Input
          testId="BondTicketDV01Input"
          value={returnTicketDV01(TicketInstrument.Bond, ticket)}
          onCommit={(dv01) =>
            updateBondTicket(
              returnDV01SizeChanges(TicketInstrument.Bond, ticket, dv01) as Partial<IBondTicket>,
            )
          }
          disabledReason={
            returnTicketRisk(TicketInstrument.Bond, ticket) === null ? NO_RISK_MESSAGE : undefined
          }
        />
      </TicketRow>

      <AllocationSplits
        testIdPrefix="BondTicket"
        portfolioCode={ticket.portfolioCode}
        onPortfolioChange={(portfolioCode) => updateBondTicket({ portfolioCode })}
        isPortfolioRequired={isRequired("portfolioCode")}
        total={ticket.nominal}
        splits={ticket.additionalAllocations}
        sizeKind="nominal"
        highlightedKeys={highlightedKeys}
        onChange={(additionalAllocations) => updateBondTicket({ additionalAllocations })}
      />

      <TicketRow
        label="Direction"
        testId="BondTicketDirectionRow"
        isHighlighted={isMissing("direction")}
        isRequired={isRequired("direction")}
      >
        <DirectionSelect
          testId="BondTicketDirectionInput"
          value={ticket.direction}
          onChange={(direction) => updateBondTicket({ direction })}
        />
      </TicketRow>

      <TicketRow
        label="Settlement Date"
        testId="BondTicketSettlementDateRow"
        isHighlighted={isMissing("settlementDate")}
        isRequired={isRequired("settlementDate")}
      >
        <TicketDateInput
          testId="BondTicketSettlementDateInput"
          holidayCalendars={returnSettlementHolidayCalendars(TicketInstrument.Bond, ticket)}
          value={ticket.settlementDate}
          onChange={(settlementDate) => updateBondTicket({ settlementDate })}
        />
      </TicketRow>

      <TicketExternalIdRow
        testIdPrefix="BondTicket"
        value={ticket.transactionNumber}
        onChange={(transactionNumber) => updateBondTicket({ transactionNumber })}
      />
    </div>
  );
};

export default BondCoreFields;
